/**
 * Trip domain contracts — the central aggregate of Module 1 (TW-3437).
 *
 * As with the admin contracts, the Zod schemas here are the single source of truth for
 * request validation: the API validates bodies with them (ZodValidationPipe) and the web
 * forms validate against the very same objects — one rule, two enforcement points
 * (architecture §4.1). The status state machine lives here too so both the service layer
 * (which enforces it) and the UI (which only offers legal next states) share one map
 * (architecture §4.2).
 *
 * Field set mirrors the `trip` table (data-model §5). The genuinely variable parts
 * (transport modes, baggage, room config, extra services) are JSONB columns, modelled here
 * as small typed shapes so the wizard (TW-3438) and the API agree on their structure.
 */
import { z } from "zod";
import { TRIP_STATUSES, type TripStatus } from "./constants";
import { SPORT_NAMES } from "./sport";

// ── Trip list pagination + sorting (server-side) ──────────────────────────────
//
// The Trips tables (club TripsPage + back-office OpsTripsPage) page + sort on the server so they
// stay fast as a club accrues hundreds of trips. The same `GET /api/trips` endpoint still returns
// the full array when no `page` is given (the dashboards/calendar fold over it); passing `page`
// switches it to the `TripListPage` envelope below.
//
// Sort fields map to columns that are coalesced for display, approximated with nulls-last
// multi-column ordering (no raw SQL): `trip` → matchOpponent then sport; `destination` → city then
// country; `dates` → travelStartDate then travelEndDate; `reference` → reference.
export const TRIP_SORT_FIELDS = ["reference", "trip", "destination", "dates"] as const;
export type TripSortField = (typeof TRIP_SORT_FIELDS)[number];

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/** Default rows per page for the Trips tables. */
export const DEFAULT_TRIP_PAGE_SIZE = 20;
/** Upper bound so a caller can't ask for an unbounded page. */
export const MAX_TRIP_PAGE_SIZE = 100;

/**
 * Query params for the paginated/sorted trip list. All optional: with no `page` the endpoint
 * returns the full array (back-compat). `status` mirrors the UI filter — a concrete status, or
 * `"active"` (non-terminal); absent = "All", which excludes Completed trips (TRAV-76). Numbers are
 * coerced from the query string.
 */
export const tripListQuerySchema = z.object({
  clubId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(MAX_TRIP_PAGE_SIZE).optional(),
  sort: z.enum(TRIP_SORT_FIELDS).optional(),
  dir: z.enum(SORT_DIRECTIONS).optional(),
  status: z.union([z.enum(TRIP_STATUSES), z.literal("active")]).optional(),
  // Inclusive travel-start-date range (back-office trips table "Depart from / to" filter). Each is a
  // date-only `YYYY-MM-DD`; a trip with no start date is excluded once either bound is set.
  startFrom: z.iso.date().optional(),
  startTo: z.iso.date().optional(),
});
export type TripListQuery = z.infer<typeof tripListQuerySchema>;

/** One page of trips plus the metadata the table needs to render its pager. */
export interface TripListPage {
  items: TripDto[];
  /** Total rows matching the filter (across all pages). */
  total: number;
  page: number;
  pageSize: number;
}

// ── Trip status state machine (architecture §4.2, data-model §9) ──────────────
//
// Enquiry → ProposalSent → Accepted → Completed, with Cancelled/Postponed reachable from any
// active (non-terminal) state. Accepted (the club confirmed their selection) is the agreed
// state from which the trip later completes — there is no separate "Confirmed" step. Completed
// and Cancelled are terminal. Postponed can resume back to Enquiry (or be cancelled outright).
// The service rejects any transition not listed here; the UI renders only the listed targets.
//
// Proposal lifecycle additions (TW UAT round 1):
//  • ProposalSent → Expired — the acceptance deadline lapsed (the auto-expiry job, system actor).
//  • Accepted → ProposalSent — the back office amends an accepted proposal, restarting club review
//    (revise → edit → re-send, which moves the trip back).
//  • Expired → Enquiry — the club resubmits, restarting the proposal-building process (else Cancel).
//  • Expired → ProposalSent — the back office revises a lapsed proposal and re-sends it directly
//    (revise → edit → re-send, mirroring the Accepted amend path; TRAV-65).
export const TRIP_STATUS_TRANSITIONS: Record<TripStatus, readonly TripStatus[]> = {
  Enquiry: ["ProposalSent", "Cancelled", "Postponed"],
  ProposalSent: ["Accepted", "Expired", "Cancelled", "Postponed"],
  Accepted: ["ProposalSent", "Completed", "Cancelled", "Postponed"],
  Completed: [],
  Cancelled: [],
  Postponed: ["Enquiry", "Cancelled"],
  Expired: ["Enquiry", "ProposalSent", "Cancelled"],
};

/** Whether `to` is a legal next status from `from`. */
export function canTransitionTrip(from: TripStatus, to: TripStatus): boolean {
  return TRIP_STATUS_TRANSITIONS[from].includes(to);
}

/** Legal next statuses from `from` (empty for terminal states). */
export function nextTripStatuses(from: TripStatus): readonly TripStatus[] {
  return TRIP_STATUS_TRANSITIONS[from];
}

/**
 * Narrow a stored trip status onto the app domain `TripStatus`. The database `trip_status` enum (and
 * the Prisma client generated from it) still carries the retired `Confirmed` value — it was removed
 * from the application without a migration, so no live row can be `Confirmed`. Fold any such legacy
 * value onto its successor, `Accepted` (the agreed state that replaced "Confirmed"), so Prisma values
 * cross into the domain type-safely. Takes a plain string so callers needn't import the Prisma enum.
 */
export function toDomainTripStatus(status: string): TripStatus {
  return (status === "Confirmed" ? "Accepted" : status) as TripStatus;
}

// ── JSONB sub-shapes (data-model §5) ──────────────────────────────────────────

/** Preferred transport modes for the trip. */
export const transportModesSchema = z
  .object({
    flight: z.boolean(),
    train: z.boolean(),
    coach: z.boolean(),
    hireCar: z.boolean(),
  })
  .partial();
export type TransportModes = z.infer<typeof transportModesSchema>;

/** Special / oversized baggage requirements (free-text note for now). */
export const baggageRequirementsSchema = z.object({
  notes: z.string().trim().max(2000),
});
export type BaggageRequirements = z.infer<typeof baggageRequirementsSchema>;

/** Room configuration, e.g. `{ single: 2, twin: 8, triple: 1 }`. */
export const roomConfigSchema = z
  .object({
    single: z.number().int().min(0).max(999),
    double: z.number().int().min(0).max(999),
    twin: z.number().int().min(0).max(999),
    triple: z.number().int().min(0).max(999),
  })
  .partial();
export type RoomConfig = z.infer<typeof roomConfigSchema>;

/** Closed set of optional extras a club can request (mirrors the request wizard). */
export const EXTRA_SERVICES = [
  "meetingRoom",
  "projector",
  "massageTable",
  "iceRecovery",
  "laundry",
  "meals",
] as const;
export type ExtraService = (typeof EXTRA_SERVICES)[number];
export const extraServicesSchema = z.array(z.enum(EXTRA_SERVICES));

export const BOARD_BASES = ["RO", "BB", "HB", "FB", "AI"] as const;
export type BoardBasis = (typeof BOARD_BASES)[number];

// ── Trip request body ─────────────────────────────────────────────────────────
//
// Every domain field is optional because the corresponding column is nullable (data-model
// §5): a club submits whatever it knows at enquiry time and TC fills the gaps. `status`,
// `reference`, `clubId` (for club users) and `createdBy` are server-controlled and never
// accepted from the body. A back-office user may target a club via `clubId`; for a club
// user the field is ignored in favour of their own tenant context.
const tripFields = {
  sport: z.string().trim().max(120).optional(),
  matchOpponent: z.string().trim().max(200).optional(),
  competition: z.string().trim().max(200).optional(),
  matchDate: z.iso.date().optional(),
  destinationCity: z.string().trim().max(120).optional(),
  destinationCountry: z.string().trim().max(120).optional(),
  travelStartDate: z.iso.date().optional(),
  travelEndDate: z.iso.date().optional(),
  paxCount: z.number().int().min(0).max(100000).optional(),
  /** Optional breakdown of `paxCount`: travelling players and staff (data-model §5). */
  playerCount: z.number().int().min(0).max(100000).optional(),
  staffCount: z.number().int().min(0).max(100000).optional(),
  transportModes: transportModesSchema.optional(),
  baggageRequirements: baggageRequirementsSchema.optional(),
  roomConfig: roomConfigSchema.optional(),
  boardBasis: z.enum(BOARD_BASES).optional(),
  extraServices: extraServicesSchema.optional(),
  /** Free-text notes for TC (data-model §5: `notes` column). */
  notes: z.string().trim().max(4000).optional(),
  /** Free-text hotel preferences at enquiry time — stars/budget/area (`hotel_preference_notes`). */
  hotelPreferenceNotes: z.string().trim().max(2000).optional(),
};

export const createTripSchema = z.object({
  // Back-office only — which club to create the trip for. Ignored for club users.
  clubId: z.uuid().optional(),
  ...tripFields,
});
export type CreateTripInput = z.infer<typeof createTripSchema>;

// ── Club trip-request submission (TW-3438) ────────────────────────────────────
//
// `createTripSchema` is intentionally lenient — every domain field is optional so the API
// (and a back-office user) can create a sparse trip and fill the gaps later. The club
// *request wizard*, however, demands a usable enquiry: an opponent, a destination, travel
// dates, a pax count, and at least one transport mode. This stricter schema is the single
// source of truth for that rule — the web form drives its `zodResolver` from it AND
// `TripsService.create` re-validates against it for club users (architecture §4.1: one rule,
// two enforcement points). Back-office creates remain governed by the lenient schema.
export const submitTripRequestSchema = z
  .object({
    ...tripFields,
    // The wizard's sport Select only offers catalogue sports (values = canonical names) plus an
    // empty "no sport" choice — enforce the same set here. `""` is the form's "no sport" value
    // (dropped from the payload); `trip.sport` itself stays optional (fallback ref code GEN).
    sport: z.union([z.enum(SPORT_NAMES), z.literal("")]).optional(),
    matchOpponent: z.string().trim().min(1, "An opponent or fixture is required").max(200),
    destinationCity: z.string().trim().min(1, "A destination is required").max(120),
    travelStartDate: z.iso.date(),
    travelEndDate: z.iso.date(),
    paxCount: z.number().int().min(1, "At least one traveller is required").max(100000),
    transportModes: transportModesSchema,
  })
  .refine((v) => Boolean(v.transportModes.flight || v.transportModes.train || v.transportModes.coach || v.transportModes.hireCar), {
    message: "Select at least one transport mode",
    path: ["transportModes"],
  })
  .refine((v) => v.travelEndDate >= v.travelStartDate, {
    message: "Return date must be on or after the departure date",
    path: ["travelEndDate"],
  });
export type SubmitTripRequestInput = z.infer<typeof submitTripRequestSchema>;

export const updateTripSchema = z
  .object(tripFields)
  .refine((v) => Object.keys(v).length > 0, { message: "no fields to update" });
export type UpdateTripInput = z.infer<typeof updateTripSchema>;

/** Status transition request — the target status (validated against the map server-side). */
export const transitionTripSchema = z.object({
  status: z.enum(TRIP_STATUSES),
});
export type TransitionTripInput = z.infer<typeof transitionTripSchema>;

// ── Response DTO ────────────────────────────────────────────────────────────────
/**
 * A trip's proposal economics, folded from its latest proposal's line items (TW back-office trips
 * table). **Back-office only** — a club never receives this (margin is TC-internal); the list
 * endpoint leaves {@link TripDto.finance} `null` for club users. `null` also when the trip has no
 * priced proposal yet (an Enquiry, or a proposal with no lines).
 */
export interface TripFinanceDto {
  /** Σ client (sell) total across the proposal's lines, whole euros. */
  sellTotal: number;
  /** Σ net (cost) total across the lines. */
  netCost: number;
  /** sellTotal − netCost. */
  margin: number;
  /** Markup on cost: (sellTotal / netCost − 1) × 100, to one decimal; 0 when netCost is 0. */
  marginPct: number;
}

export interface TripDto {
  id: string;
  clubId: string;
  reference: string;
  sport: string | null;
  matchOpponent: string | null;
  competition: string | null;
  matchDate: string | null;
  destinationCity: string | null;
  destinationCountry: string | null;
  travelStartDate: string | null;
  travelEndDate: string | null;
  paxCount: number | null;
  playerCount: number | null;
  staffCount: number | null;
  transportModes: TransportModes | null;
  baggageRequirements: BaggageRequirements | null;
  roomConfig: RoomConfig | null;
  boardBasis: BoardBasis | null;
  extraServices: ExtraService[] | null;
  notes: string | null;
  hotelPreferenceNotes: string | null;
  /** When the club last submitted its passenger list to TC, or null if never (TW user-testing). */
  manifestSubmittedAt: string | null;
  /** When the manifest last changed (assign/unassign), or null if never assigned. */
  manifestUpdatedAt: string | null;
  status: TripStatus;
  /** When the trip was moved to `Expired` (deadline lapsed), or null. Drives the 7-day active window. */
  expiredAt: string | null;
  /**
   * When the club amended the request after a proposal went out (ProposalSent/Accepted), or null.
   * Cleared when TC publishes the next proposal version. Drives {@link isAmendedTrip}.
   */
  amendedAt: string | null;
  /** Legal next statuses from the current one — lets the UI render only valid actions. */
  allowedTransitions: TripStatus[];
  /** Back-office operator who owns this trip's worklist item, or null while unassigned (TW-3440). */
  ownerUserId: string | null;
  /** Proposal economics for the back-office trips table; null for club users / unpriced trips. */
  finance: TripFinanceDto | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Assign owner (TW-3440, back-office only) ──────────────────────────────────
//
// Assigning a TC operator to a trip is a dedicated back-office action with its own audit
// trail, kept off the generic create/update path. `ownerUserId` is nullable so the same
// endpoint can also clear an assignment (hand a trip back to the unassigned queue).
export const assignTripOwnerSchema = z.object({
  ownerUserId: z.uuid().nullable(),
});
export type AssignTripOwnerInput = z.infer<typeof assignTripOwnerSchema>;

/** A TC operator who can own a trip — the assign pick-list (active back-office users). */
export interface AssignableOwnerDto {
  id: string;
  name: string;
}

// ── Dashboard derivation (TW-3439) ────────────────────────────────────────────
//
// The club dashboard's KPIs and "upcoming travel" feed are derived purely from the
// RLS-scoped trip list the API already returns (GET /api/trips). There is no separate
// metrics endpoint and no proposal-deadline data yet (that lands with Module 3), so the
// numbers a club sees are exactly a fold over its own trips. These helpers live here — next
// to the status machine — so the same definition of "active" drives both the web summary and
// its tests, and the web never re-derives the shape.

/** Terminal statuses — a trip in one of these is finished with no outgoing transitions. */
export const TERMINAL_TRIP_STATUSES: readonly TripStatus[] = ["Completed", "Cancelled"];

/**
 * How long a trip stays "active" after its proposal acceptance deadline lapses (TW-3526 follow-up).
 * An `Expired` trip is stalled, not live — but a recently expired one is still actionable (the club
 * may resubmit it), so it remains in the active list/KPIs for this many days before dropping out, so
 * the list isn't cluttered with stale expired trips. The same window drives the web list filter (a
 * `where` on `expiredAt`), `isActiveTrip`, and the active KPIs — one rule, everywhere.
 */
export const EXPIRED_ACTIVE_WINDOW_DAYS = 7;

/**
 * Whether a trip is still "active". Not active when terminal (Completed/Cancelled). An `Expired`
 * trip counts as active only while it expired within {@link EXPIRED_ACTIVE_WINDOW_DAYS} of `nowIso`
 * (an Expired row with no `expiredAt` — e.g. pre-backfill legacy — is treated as long-expired, so
 * inactive). Everything else (Enquiry/ProposalSent/Accepted/Postponed) is active.
 */
export function isActiveTrip(trip: Pick<TripDto, "status" | "expiredAt">, nowIso: string): boolean {
  if (TERMINAL_TRIP_STATUSES.includes(trip.status)) return false;
  if (trip.status === "Expired") {
    if (!trip.expiredAt) return false;
    const cutoff = new Date(nowIso).getTime() - EXPIRED_ACTIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return new Date(trip.expiredAt).getTime() >= cutoff;
  }
  return true;
}

/**
 * Whether a trip's travel dates are both in the past while it is still open (TRAV-76 follow-up):
 * the trips tables flag such rows so someone marks them Completed (or otherwise resolves them).
 * Terminal trips (Completed/Cancelled) never qualify, nor does a trip missing either date.
 * `todayIso` is the viewer's local calendar date (`YYYY-MM-DD`); travel dates are date-only, so a
 * plain string compare is a correct day comparison.
 */
export function hasTravelDatesPassed(
  trip: Pick<TripDto, "status" | "travelStartDate" | "travelEndDate">,
  todayIso: string,
): boolean {
  if (TERMINAL_TRIP_STATUSES.includes(trip.status)) return false;
  if (!trip.travelStartDate || !trip.travelEndDate) return false;
  return trip.travelStartDate < todayIso && trip.travelEndDate < todayIso;
}

/**
 * Active trips with a travel start date today or later, soonest first — the source for the
 * dashboard's "upcoming travel" feed. Trips without a start date are excluded (nothing to sort or
 * count down to), and `Expired` trips are excluded outright (even within the active window): the
 * feed is for genuinely-live travel, not stalled trips awaiting reactivation.
 */
export function upcomingTrips(trips: readonly TripDto[], nowIso: string): TripDto[] {
  const today = startOfDay(nowIso);
  return trips
    .filter(
      (t) =>
        isActiveTrip(t, nowIso) &&
        t.status !== "Expired" &&
        t.travelStartDate &&
        startOfDay(t.travelStartDate) >= today,
    )
    .sort((a, b) => (a.travelStartDate ?? "").localeCompare(b.travelStartDate ?? ""));
}

// ── Back-office cross-club derivation (TW-3440) ───────────────────────────────
//
// The TC operations dashboard and worklist are the back-office counterpart to the club
// dashboard: same "fold over the trip list" approach, but over the cross-club list the API
// returns to back office (RLS-bypassed) rather than a single club's. As with the club
// summary, `nowIso` is passed in so the result is deterministic and testable, and there is
// still no proposal-deadline data until Module 3 — the "at risk" signal is honestly backed
// by imminent *travel* departures, not faked proposal deadlines.

/**
 * Whether a trip is a fresh, untriaged request: still an `Enquiry` and not yet assigned to a
 * TC operator. This is derived state (no per-user read/unread table) — a new club enquiry is
 * "fresh" until someone takes ownership of it.
 */
export function isFreshRequest(trip: Pick<TripDto, "status" | "ownerUserId">): boolean {
  return trip.status === "Enquiry" && !trip.ownerUserId;
}

// ── Club trip editing (TW UAT round 3 follow-up) ──────────────────────────────
//
// A club may keep refining its request after TC has responded — typically adding players/staff
// or extras — right up until the trip completes. Editing never moves the trip's status by
// itself; instead an edit at ProposalSent/Accepted stamps `amendedAt` (the back-office
// "Amended" marker, cleared when TC publishes the next proposal version), and an edit at
// Expired doubles as the resubmission (Expired → Enquiry, server-side). Terminal and parked
// states (Completed/Cancelled/Postponed) stay read-only.
export const CLUB_EDITABLE_TRIP_STATUSES: readonly TripStatus[] = [
  "Enquiry",
  "ProposalSent",
  "Accepted",
  "Expired",
];

/** Whether a club user may edit the trip request in this status. */
export function canClubEditTrip(status: TripStatus): boolean {
  return CLUB_EDITABLE_TRIP_STATUSES.includes(status);
}

/**
 * Whether the trip carries a live club amendment TC hasn't answered yet — the back-office
 * "Amended" marker. Derived like {@link isFreshRequest}: status-gated so a trip that is later
 * cancelled/postponed/completed stops badging without the column needing a clear.
 */
export function isAmendedTrip(trip: Pick<TripDto, "status" | "amendedAt">): boolean {
  return trip.amendedAt != null && (trip.status === "ProposalSent" || trip.status === "Accepted");
}

/**
 * The request queue: enquiries awaiting a proposal, sorted by travel start date ascending
 * (soonest departure first) — the same ordering the club dashboard uses for its trip list.
 * Enquiries without a travel start date sort to the end.
 */
export function requestQueue(trips: readonly TripDto[]): TripDto[] {
  return trips
    .filter((t) => t.status === "Enquiry")
    .sort((a, b) => (a.travelStartDate ?? "9999-12-31").localeCompare(b.travelStartDate ?? "9999-12-31"));
}

// ── Activity feed (TW-3440) ───────────────────────────────────────────────────
//
// The operations dashboard shows recent cross-club activity, read from the append-only audit
// log (back office sees every club's rows under RLS). The DTO keeps just enough to render a
// localized line — the action + the trip/club it touched — without leaking internal detail.
export interface ActivityEntryDto {
  id: string;
  /** Audit action, e.g. `status_change`, `owner_change`. */
  action: string;
  entityType: string;
  entityId: string;
  clubId: string | null;
  clubName: string | null;
  /** The trip's reference (e.g. `RCPB-HK-26-0001`; legacy `TC-YYYY-NNNN`) when the entity is a trip, else null. */
  tripReference: string | null;
  /** The post-change status when the action carried one (for status_change lines). */
  status: TripStatus | null;
  createdAt: string;
}

/** Midnight-truncated epoch ms for an ISO date/datetime, for whole-day comparisons. */
function startOfDay(iso: string): number {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
